// Capacitor 네이티브 브리지 (§12). 웹에선 안전하게 no-op/폴백.
// 플러그인은 웹 구현이 있어 import는 웹에서도 안전.

import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle, NotificationType } from "@capacitor/haptics";
import { Share } from "@capacitor/share";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Media } from "@capacitor-community/media";

export function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

// 앱인토스(토스 미니앱) 빌드인지. bwek-app(스토어/웹)에선 항상 false — bwek-toss는 자체 native.ts에서 true.
// 토스는 '자사 앱 설치 유도' 금지라 hyuh 크로스프로모를 토스 빌드에선 렌더하지 않기 위해 사용.
export function isToss(): boolean {
  return false;
}

const ALBUM = "Bwek";

// Android는 savePhoto에 albumIdentifier가 필수 → "Bwek" 앨범을 찾거나 만들어 식별자 반환.
// iOS는 불필요(add-only 권한으로 카메라롤에 저장).
async function androidAlbumId(): Promise<string | undefined> {
  if (Capacitor.getPlatform() !== "android") return undefined;
  const { albums } = await Media.getAlbums();
  let a = albums.find((x) => x.name === ALBUM);
  if (!a) {
    await Media.createAlbum({ name: ALBUM });
    a = (await Media.getAlbums()).albums.find((x) => x.name === ALBUM);
  }
  return a?.identifier;
}

/** 공유 카드 PNG를 기기 갤러리에 저장. dataURL을 그대로 넘긴다(저장은 임시파일 불필요). */
export async function nativeSaveImage(dataUrl: string): Promise<boolean> {
  if (!isNative()) return false;
  try {
    const albumIdentifier = await androidAlbumId();
    await Media.savePhoto({ path: dataUrl, albumIdentifier, fileName: `bwek-${Date.now()}` });
    return true;
  } catch {
    return false;
  }
}

/** 공유 카드 PNG를 네이티브 공유 시트로(이미지 파일 첨부). 캐시에 쓰고 file URI를 공유.
 *  url은 안 넘긴다 — 넘기면 일부 타깃이 '링크 공유'로 처리해 이미지를 떨군다. 링크는 text 안에 둔다. */
export async function nativeShareImage(dataUrl: string, text: string): Promise<boolean> {
  if (!isNative()) return false;
  try {
    const base64 = dataUrl.includes(",") ? dataUrl.split(",")[1] : dataUrl;
    const name = `bwek-${Date.now()}.png`;
    const { uri } = await Filesystem.writeFile({ path: name, data: base64, directory: Directory.Cache });
    await Share.share({ title: "bwek", text, files: [uri], dialogTitle: "bwek" });
    return true;
  } catch (e) {
    console.error("nativeShareImage failed:", e);
    return false;
  }
}

/** 공유 카드 영상(mp4, base64)을 기기 갤러리에 저장. 캐시에 쓰고 그 file URI를 saveVideo에 넘긴다. */
export async function nativeSaveVideo(base64Mp4: string): Promise<boolean> {
  if (!isNative()) return false;
  try {
    const name = `bwek-${Date.now()}.mp4`;
    const { uri } = await Filesystem.writeFile({ path: name, data: base64Mp4, directory: Directory.Cache });
    const albumIdentifier = await androidAlbumId();
    await Media.saveVideo({ path: uri, albumIdentifier, fileName: name.replace(/\.mp4$/, "") });
    return true;
  } catch (e) {
    console.error("nativeSaveVideo failed:", e);
    return false;
  }
}

/** 공유 카드 영상(mp4, base64)을 네이티브 공유 시트로(영상 파일 첨부). 이미지 공유와 동일 패턴. */
export async function nativeShareVideo(base64Mp4: string, text: string): Promise<boolean> {
  if (!isNative()) return false;
  try {
    const name = `bwek-${Date.now()}.mp4`;
    const { uri } = await Filesystem.writeFile({ path: name, data: base64Mp4, directory: Directory.Cache });
    await Share.share({ title: "bwek", text, files: [uri], dialogTitle: "bwek" });
    return true;
  } catch (e) {
    console.error("nativeShareVideo failed:", e);
    return false;
  }
}

/** 구겨 버리기 순간 햅틱(촉각 카타르시스). */
export function hapticCrumple(): void {
  if (!isNative()) return;
  Haptics.impact({ style: ImpactStyle.Medium }).catch(() => {});
}

/** 판정 등장(도장 쾅) 햅틱. */
export function hapticStamp(): void {
  if (!isNative()) return;
  Haptics.notification({ type: NotificationType.Success }).catch(() => {});
}

/** 네이티브 공유 시트(텍스트+URL). 이미지 파일 공유는 Filesystem 연동 시 확장. */
export async function nativeShare(text: string, url = "https://bwek.app"): Promise<boolean> {
  if (!isNative()) return false;
  try {
    await Share.share({ text, url });
    return true;
  } catch {
    return false;
  }
}
